import { useEffect, useState } from 'react';

export interface LogoAccent {
  color: string;
  ink: string;
}

export function useLogoAccent(src: string, fallback = '#f15c21'): LogoAccent {
  const fallbackAccent = { color: fallback, ink: '#ffffff' };
  const [accent, setAccent] = useState<LogoAccent>(fallbackAccent);

  useEffect(() => {
    let cancelled = false;
    setAccent(fallbackAccent);
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 36;
        canvas.height = 36;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        const buckets = new Map<string, {
          score: number;
          red: number;
          green: number;
          blue: number;
          count: number;
        }>();

        for (let index = 0; index < pixels.length; index += 4) {
          const alpha = pixels[index + 3]! / 255;
          if (alpha < 0.55) continue;
          const red = pixels[index]!;
          const green = pixels[index + 1]!;
          const blue = pixels[index + 2]!;
          const maximum = Math.max(red, green, blue) / 255;
          const minimum = Math.min(red, green, blue) / 255;
          const lightness = (maximum + minimum) / 2;
          const chroma = maximum - minimum;
          const saturation = chroma === 0 ? 0 : chroma / (1 - Math.abs(2 * lightness - 1));
          if (lightness < 0.1 || lightness > 0.9 || saturation < 0.18) continue;

          const key = `${Math.round(red / 32)}:${Math.round(green / 32)}:${Math.round(blue / 32)}`;
          const bucket = buckets.get(key) ?? { score: 0, red: 0, green: 0, blue: 0, count: 0 };
          bucket.score += alpha * (0.7 + saturation);
          bucket.red += red;
          bucket.green += green;
          bucket.blue += blue;
          bucket.count += 1;
          buckets.set(key, bucket);
        }

        const primary = [...buckets.values()].sort((left, right) => right.score - left.score)[0];
        if (!primary || cancelled) return;
        const red = Math.round(primary.red / primary.count);
        const green = Math.round(primary.green / primary.count);
        const blue = Math.round(primary.blue / primary.count);
        const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
        setAccent({
          color: `rgb(${red} ${green} ${blue})`,
          ink: luminance > 0.58 ? '#171717' : '#ffffff',
        });
      } catch {
        // Cross-origin artwork without canvas permission keeps the fallback.
      }
    };
    image.src = src;
    return () => { cancelled = true; };
  }, [src, fallback]);

  return accent;
}
